import React, { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
  useFeatures,
  useCreateFeature,
  useEditFeature,
  useDeleteFeature,
} from "hooks/featureHook"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"
import { useModels } from "hooks/modelHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import BadgeList from "components/Common/BadgeList"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const FeatureList = () => {
  document.title = "Feature List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [feature, setFeature] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      with_models: true,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: featuresResponse,
    isLoading,
    refetch: featureRefetch,
    isRefetching: featureRefetchLoading,
  } = useFeatures(queryParams)

  const { data: modelsResponse, isLoading: modelsFetchLoading } = useModels(
    { all: true },
    modal
  )

  const { mutateAsync: createFeature, isPending: featureCreateLoading } =
    useCreateFeature()
  const { mutateAsync: editFeature, isPending: featureEditLoading } =
    useEditFeature()
  const { mutateAsync: deleteFeature, isPending: featureDeleteLoading } =
    useDeleteFeature()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setFeature(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFeature(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleFeatureClick = useCallback(
    arg => {
      setFeature({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(featureData => {
    setFeature(featureData)
    setDeleteModal(true)
  }, [])

  const handleDeleteFeature = useCallback(async () => {
    if (feature?.id) {
      try {
        const res = await deleteFeature(feature.id)
        toast.success(res?.message ?? "Feature deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete feature:", error)
        toast.error("Failed to delete feature.")
      }
    }
  }, [feature, deleteFeature])

  const handleFeatureListRefresh = () => {
    featureRefetch()
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter feature name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name,
        modelIds: values.selectedModels?.map(n => Number(n)),
      }
      const action = isEdit
        ? editFeature({ id: feature.id, ...payload })
        : createFeature(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Feature ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save feature:", error)
        toast.error("Failed to save feature.")
      }
    },
    [isEdit, feature, editFeature, createFeature, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: feature?.name || "",
      selectedModels: modelToModelIdsNumber(feature?.models) || [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Name", accessorKey: "name" },
      { header: "Slug", accessorKey: "slug" },
      {
        header: "Models",
        accessorKey: "models",
        cell: ({ row }) => <BadgeList items={row.original.models} />,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleFeatureClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleFeatureClick, onClickDelete]
  )

  const featuresData = featuresResponse?.data?.data || []
  const paginationInfo = featuresResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteFeature}
        loading={featureDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Feature" breadcrumbItem="Feature Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">
                      Feature Lists
                    </h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Feature
                      </Button>
                      <Button
                        color="light"
                        onClick={handleFeatureListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            featureRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                        >
                          <i className="mdi mdi-close" />
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={featuresData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || featureRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Feature" : "Add Feature"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Feature Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter feature name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label>Select Models for This Feature</Label>
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={modelsResponse?.data}
                      name="selectedModels"
                      selected={validation.values.selectedModels}
                      onChange={newSelected =>
                        validation.setFieldValue("selectedModels", newSelected)
                      }
                      loading={modelsFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={featureEditLoading || featureCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Feature"}
                    {(featureEditLoading || featureCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default FeatureList
